import { Check, ArrowRight } from "lucide-react";
import { orderedCategories, school, singleLessonPrices, lessonCount, extraFeatures } from "../mock/mock";
import CTASection from "../components/CTASection";
import useSeo from "../hooks/useSeo";
import { CAMPAIGN, campaignActive } from "../lib/campaign";

export default function Priser() {
  const kampanj = campaignActive();
  useSeo({ title: "Paket och priser", description: "Alla paket och priser hos City Trafikskola Uppsala: startpaket, körpaket och intensivpaket för B-körkort. Tydliga priser inklusive moms – boka online." });
  return (
    <main className="pt-20 md:pt-[8.25rem]">
      {/* Header */}
      <section className="bg-[#0B2545] py-20">
        <div className="max-w-7xl mx-auto px-5 text-center">
          <span className="text-[#ffce3a] font-semibold tracking-[0.2em] text-xs uppercase">Paket och priser</span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white mt-3">Hitta paketet som passar dig</h1>
          <p className="text-slate-300 mt-4 max-w-2xl mx-auto">Vi erbjuder startpaket, körpaket och intensivpaket. Alla priser är inklusive moms.</p>
          {kampanj && <p className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ffce3a] text-[#0B2545] text-sm font-extrabold" data-testid="priser-campaign-note">{CAMPAIGN.label}: {CAMPAIGN.percent} % på {CAMPAIGN.scopeText} {CAMPAIGN.periodText} – uppge koden <span className="px-2 py-0.5 rounded bg-[#0B2545] text-[#ffce3a] tracking-[0.15em]">{CAMPAIGN.code}</span> vid köp online</p>}
          {kampanj && <p className="mt-3 text-xs text-slate-400" data-testid="priser-campaign-terms">{CAMPAIGN.terms}</p>}
        </div>
      </section>

      {orderedCategories.map((cat, ci) => (
        <section key={cat.key} className={ci % 2 ? "bg-[#f6f9fc] py-20" : "py-20"}>
          <div className="max-w-7xl mx-auto px-5">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-[#1B6CA8] font-semibold tracking-[0.18em] text-xs uppercase">{cat.title}</span>
              <h2 className="font-display text-3xl font-bold text-[#0B2545] mt-2">{cat.title}</h2>
              <p className="text-slate-500 mt-3">{cat.subtitle}</p>
            </div>
            <div className={`grid gap-6 ${cat.items.length >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" : cat.items.length === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
              {cat.items.map((p) => (
                <div key={p.name} className={`relative flex flex-col bg-white rounded-2xl p-7 border transition-all hover:-translate-y-1 ${p.popular ? "border-[#ffce3a] shadow-xl shadow-amber-100/60" : "border-slate-100 hover:shadow-lg"}`}>
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#ffce3a] text-[#0B2545] text-[11px] font-bold whitespace-nowrap">Populärast</span>
                  )}
                  <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{p.name}</h3>
                  <div className="mt-2 flex items-end gap-2.5">
                    <span className="font-display text-7xl leading-[0.85] font-black tracking-tight text-[#0B2545]" data-testid="price-lesson-count">{lessonCount(p)}</span>
                    <span className="pb-1 text-sm font-extrabold text-[#0B2545] leading-tight">körlektioner<br /><span className="text-xs font-semibold text-slate-500">à {cat.duration || 60} minuter</span></span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 font-display text-2xl font-black tracking-tight text-[#0B2545]" data-testid="price-value">{p.price}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-400 line-through">Ord. pris {p.ord}</span>
                    <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-extrabold">Du sparar {p.save.replace("Rabatt ", "")}</span>
                  </div>
                  <ul className="mt-4 space-y-2.5 flex-1">
                    {extraFeatures(p).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[13px] text-slate-600">
                        <Check size={16} className="text-[#1B6CA8] mt-0.5 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={school.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#0B2545] text-white text-sm font-semibold hover:bg-[#13315C] transition-colors"
                  >
                    Välj paket
                    <ArrowRight size={16} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Info */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h3 className="font-display font-bold text-xl text-[#0B2545] mb-2">Bra att veta</h3>
          <p className="text-slate-500">
            Alla priser är inklusive moms. Har du frågor om intensivutbildning eller vill ha ett anpassat upplägg? Maila oss på {" "}
            <a href={`mailto:${school.email}`} className="text-[#1B6CA8] font-semibold hover:underline">{school.email}</a>.
          </p>
          <a
            href={school.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-xl bg-[#ffce3a] text-[#0B2545] font-semibold hover:bg-[#ffd85f] transition-colors"
          >
            Boka online
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#0B2545] text-white" data-testid="single-lessons-section">
        <div className="max-w-5xl mx-auto px-5 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-[#ffce3a] text-[#0B2545] text-[11px] font-extrabold uppercase tracking-[0.2em]">Enskilda körlektioner</span>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold mt-3">Vill du hellre köra lektion för lektion?</h2>
            <p className="text-slate-300 mt-2 text-sm md:text-base">Välj själv längd på lektionen. Paketen ovan ger alltid ett lägre pris per lektion.</p>
          </div>
          <div className="flex gap-4">
            {singleLessonPrices.map((l) => (
              <div key={l.duration} className="rounded-2xl bg-white/10 border border-white/15 px-6 py-5 text-center min-w-[140px]" data-testid={`single-lesson-${l.duration}`}>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-300">{l.duration} minuter</div>
                <div className="font-display text-3xl font-black text-[#ffce3a] mt-1">{l.price}</div>
                <div className="text-[11px] text-slate-400 mt-1">per körlektion</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
